/**
 * Retry timer utility
 *
 * Calculates exponential backoff delays for reconnection attempts.
 */

/**
 * Retry options
 */
export interface RetryOptions {
  /** Initial delay in milliseconds */
  initialDelayMs: number;

  /** Maximum delay between retries */
  maxDelayMs: number;

  /** Exponential backoff multiplier */
  backoffMultiplier: number;

  /** Maximum number of attempts (0 = infinite) */
  maxAttempts: number;
}

/**
 * Calculate delay for next retry attempt
 * Uses exponential backoff: delay = initialDelay * (multiplier ^ attemptNumber)
 *
 * @param attemptNumber - Attempt number (1-based)
 * @param options - Retry options
 * @returns Delay in milliseconds, or undefined if max attempts exceeded
 */
export function calculateRetryDelay(attemptNumber: number, options: RetryOptions): number | undefined {
  // Check if max attempts exceeded
  if (options.maxAttempts > 0 && attemptNumber > options.maxAttempts) {
    return undefined;
  }

  // Calculate exponential backoff: initial * (multiplier ^ (attempt - 1))
  const exponentialDelay =
    options.initialDelayMs * Math.pow(options.backoffMultiplier, attemptNumber - 1);

  // Cap at max delay
  const cappedDelay = Math.min(exponentialDelay, options.maxDelayMs);

  // Add small random jitter (±10%) to avoid thundering herd
  const jitter = cappedDelay * 0.1 * (Math.random() - 0.5);
  const finalDelay = Math.round(cappedDelay + jitter);

  return Math.max(finalDelay, options.initialDelayMs);
}

/**
 * Wait for specified milliseconds
 * Returns a promise that resolves after delay
 *
 * @param ms - Milliseconds to wait
 * @returns Promise that resolves after delay
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Create a retry iterator for backoff sequence
 *
 * @param options - Retry options
 * @yields Delays in milliseconds
 */
export function* createRetrySequence(options: RetryOptions): Generator<number> {
  let attempt = 1;
  while (true) {
    const delayMs = calculateRetryDelay(attempt, options);
    if (delayMs === undefined) {
      break;
    }
    yield delayMs;
    attempt++;
  }
}
